import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  Clock,
  MapPin,
  IndianRupee,
  CalendarDays,
  Pencil,
  Trash2,
  Package,
  Phone,
  User,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { cn } from '../../lib/utils';
import { ASSET_CATEGORIES, OWNERSHIP_CONFIG } from './gearConstants';
import { useDeleteAsset } from '../../hooks/useAssets';
import { toast } from 'sonner';

export default function AssetDetailSheet({ asset, open, onClose, onEdit, isReadOnly }) {
  const deleteMutation = useDeleteAsset();

  if (!asset) return null;

  const catCfg = ASSET_CATEGORIES[asset.category] || ASSET_CATEGORIES.general;
  const ownCfg = OWNERSHIP_CONFIG[asset.ownership] || OWNERSHIP_CONFIG.owned;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(asset.id);
      toast.success('Gear item deleted');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={cn('flex items-center justify-center rounded-lg h-8 w-8 text-base', catCfg.light)}>
              {catCfg.emoji}
            </div>
            <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider', catCfg.light)}>
              {catCfg.label}
            </Badge>
            <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider', ownCfg.light)}>
              {ownCfg.label}
            </Badge>
          </div>
          <SheetTitle className="text-xl font-display">{asset.name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          {/* Description */}
          {asset.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">{asset.description}</p>
          )}

          {/* Cost */}
          {asset.cost_amount != null && Number(asset.cost_amount) > 0 && (
            <div className="flex items-start gap-3">
              <IndianRupee className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm font-medium">
                {asset.cost_currency || 'INR'}{' '}
                {Number(asset.cost_amount).toLocaleString('en-IN')}
                {asset.ownership === 'rented' && ' / day'}
              </p>
            </div>
          )}

          {/* Vendor */}
          {asset.vendor_name && (
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">{asset.vendor_name}</p>
                {asset.vendor_contact && (
                  <p className="text-muted-foreground text-xs mt-0.5">{asset.vendor_contact}</p>
                )}
              </div>
            </div>
          )}

          {/* Rental dates */}
          {asset.rental_start && (
            <div className="flex items-start gap-3">
              <CalendarDays className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">Rental Period</p>
                <p className="text-muted-foreground">
                  {format(parseISO(asset.rental_start), 'MMM d, yyyy')}
                  {asset.rental_end && ` — ${format(parseISO(asset.rental_end), 'MMM d, yyyy')}`}
                </p>
                {asset.ownership === 'rented' && (
                  <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    Pickup & return events auto-synced to calendar
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Added {format(parseISO(asset.created_at), 'MMM d, yyyy h:mm a')}</p>
              {asset.updated_at !== asset.created_at && (
                <p>Updated {format(parseISO(asset.updated_at), 'MMM d, yyyy h:mm a')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex items-center gap-2 border-t pt-4">
            <Button onClick={() => onEdit(asset)} className="flex-1 gap-2">
              <Pencil className="h-4 w-4" />
              Edit Gear
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" className="shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Gear</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{asset.name}"? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
